import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Smartphone, Armchair, Gift } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StatusCard from '../components/StatusCard';
import AppDownload from '../components/AppDownload';
import { getOrder } from '../utils/api';
import { trackEvent } from '../utils/tracking';
import './Confirmation.css';

const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL;
const RETRY_DELAYS_MS = [1500, 2000];

export default function Confirmation() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackEvent('Page Viewed', { page: 'confirmation' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    const fetchOrder = async (attempt) => {
      try {
        const result = await getOrder(orderId);
        if (cancelled) return;
        setOrder(result);
        setLoading(false);
      } catch {
        if (cancelled) return;
        if (attempt < RETRY_DELAYS_MS.length) {
          setTimeout(() => fetchOrder(attempt + 1), RETRY_DELAYS_MS[attempt]);
        } else {
          setLoading(false);
        }
      }
    };

    fetchOrder(0);
    return () => { cancelled = true; };
  }, [orderId]);

  useEffect(() => {
    if (!order || !orderId) return;
    const dedupeKey = `tp_purchase_tracked_${orderId}`;
    if (sessionStorage.getItem(dedupeKey)) return;

    trackEvent('Purchase Completed', {
      tier: order.tier,
      quantity: order.quantity,
      revenue: order.amountCents / 100,
      orderId,
    });
    sessionStorage.setItem(dedupeKey, '1');
  }, [order, orderId]);

  return (
    <div className="tp-confirmation">
      <Header />
      <div className="container tp-confirmation__content">
        <h1 className="headline-1">You're all set</h1>
        <p className="body-l tp-confirmation__subhead">
          Here's what happens next — most of it already has.
        </p>

        {!loading && (
          <div className="tp-confirmation__cards">
            <StatusCard
              icon={Smartphone}
              title="Your eSIM is on its way"
              body="Check your inbox — your QR code should already be there. Scan it when you're ready. Data activates when you land."
            />
            <StatusCard
              icon={Armchair}
              title="Your lounge pass is coming separately"
              body="It arrives in its own email from our partner Lounge Pass, usually within minutes. Not seeing it? Check spam first — it likes to hide there."
            />
            <StatusCard
              icon={Gift}
              title="Your offers are ready right now"
              body="Discounts we've lined up with travel brands we actually like."
            >
              <Link to={`/offers?order=${orderId}`} className="btn btn-secondary tp-confirmation__offers-btn">
                See my offers
              </Link>
            </StatusCard>
          </div>
        )}

        {!loading && <AppDownload />}

        <p className="body-s tp-confirmation__footnote">
          {order?.email
            ? `Everything above was also sent to ${order.email}. `
            : 'Everything above was also sent to your email. '}
          Questions? {SUPPORT_EMAIL} — a real human answers.
        </p>
      </div>
      <Footer />
    </div>
  );
}
